/**
 * Stacking math for the comments gutter (#2244).
 *
 * Each gutter card wants to sit at its anchor's top. When two anchors
 * are close enough that drawing both at their natural tops would make
 * the cards overlap, the later card slides down until it clears the
 * previous one (with a gap). This function is the only place that
 * decides where cards go — the component consumes the result and
 * positions absolutely. Pure in → pure out so the AC cases (two on
 * one paragraph, five on five short paragraphs, two anchors at the
 * same offset) can be tested without any DOM.
 */

export interface CardLayoutInput {
  /** Stable key — a comment id or 'draft'. Preserved on the output. */
  key: string;
  /** Vertical position of the anchor (px), inside the scroll content. */
  anchorTop: number;
  /** Measured card height (px). Zero is tolerated — see tests. */
  height: number;
}

export interface CardLayoutOutput {
  key: string;
  anchorTop: number;
  top: number;
  /** True when the card had to slide below its anchor to clear a
   *  neighbour. The gutter uses this to decide whether to draw a
   *  connector line from the anchor up to the card. */
  pushed: boolean;
}

/**
 * Stack `cards` by anchor-top, breaking ties with the input order.
 *
 * - Order is preserved for identical anchor tops (the second card goes
 *   below the first — AC "two anchors at one offset").
 * - A card taller than the gap to the next anchor pushes successors
 *   downward (AC "card taller than the gap to the next anchor").
 * - Zero-height is permitted and acts as "no vertical footprint" — the
 *   next card can land at the same top as this one. Used by first-paint
 *   where ResizeObserver hasn't measured yet.
 *
 * `gap` is the minimum pixel distance between two cards; defaults to 8.
 */
export function stackCards(
  cards: readonly CardLayoutInput[],
  gap = 8
): CardLayoutOutput[] {
  if (cards.length === 0) return [];

  // Carry the input index through the sort so stable order wins ties.
  const indexed = cards.map((card, i) => ({ card, i }));
  indexed.sort((a, b) => {
    if (a.card.anchorTop !== b.card.anchorTop) {
      return a.card.anchorTop - b.card.anchorTop;
    }
    return a.i - b.i;
  });

  const result: CardLayoutOutput[] = [];
  let lastBottom = -Infinity;
  for (const { card } of indexed) {
    const naturalTop = card.anchorTop;
    const minTop = lastBottom === -Infinity ? naturalTop : lastBottom + gap;
    const top = Math.max(naturalTop, minTop);
    result.push({
      key: card.key,
      anchorTop: card.anchorTop,
      top,
      pushed: top > naturalTop
    });
    lastBottom = top + card.height;
  }

  return result;
}
